import { useSuperMutation } from '@vadiun/react-hooks-legacy';
import { useViajesTraslado } from 'app/viaje/services';
import {
  useDistanceAndPriceTransferReservationClientBusinessCalculator,
  TravelEstimationResult
} from 'app/viaje/services/distanceAndPriceViajeTrasladoCalculator';
import PageContainer from 'layout/components/PageContainer';
import PageHeader from 'layout/components/PageHeader';
import { useCallback, useEffect, useState } from 'react';
import { Card } from 'shared/components';
import { CreatedReservationsModal } from '../components/CreatedReservationsModal';
import { ReservaTrasladoFormWrapper } from '../components/ViajeTrasladoFormWrapper';
import TransferReservationBatchForm from '../forms/batch/TransferReservationBatchForm';
import {
  transferReservationBatchFormBuilder,
  TransferReservationBatchFormType
} from '../forms/batch/TransferReservationBatchFormType';
import { deleteDraftTravel } from 'app/viaje/services/draft';
import { BusType, transferReservationBuilder } from '../models/TransferReservation';
import { ViajeTrasladoTipoServicio } from '../models';
import { useHistory, useLocation } from 'react-router-dom';

interface TransferReservationClientBusinessBatchPageProps {
  isDraftPage?: boolean;
}

interface LocationState {
  eventId?: number;
  clientId?: number;
}

export const TransferReservationClientBusinessBatchPage = ({
  isDraftPage
}: TransferReservationClientBusinessBatchPageProps) => {
  const [createdReservationsData, setCreatedReservationsData] = useState<
    any[] | null
  >(null);
  const history = useHistory();
  const location = useLocation<LocationState>();
  const eventIdFromState = location.state?.eventId;
  const clientIdFromState = location.state?.clientId;

  const [modalOpen, setModalOpen] = useState(false);
  const [optionsBuses, setOptionsBuses] = useState<BusType[]>([]);
  const [distanceAndCost, setDistanceAndCost] = useState<
    TravelEstimationResult[]
  >([]);

  const ViajeTrasladoService = useViajesTraslado();
  const { calculateDistanceAndCost } =
    useDistanceAndPriceTransferReservationClientBusinessCalculator();
  const addTrasladoMutation = useSuperMutation(
    ViajeTrasladoService.addViajesTraslado,
    {
      showSpinner: true,
      onMutationFinish: (response: any) => {
        // Solo procesar si hay data y no hay error
        if (response?.data) {
          setCreatedReservationsData(response.data.map((r: any) => transferReservationBuilder.fromBackEnd(r)));
          setModalOpen(true);
          if (isDraftPage) {
            try {
              const draftTravel = localStorage.getItem('travel-selected-draft');
              if (draftTravel) {
                const travel = JSON.parse(draftTravel);
                deleteDraftTravel(travel._id);
              }
              localStorage.removeItem('travel-selected-draft');
            } catch (error) {
              console.error(error);
            }
          }
        }
      }
    }
  );

  const createViajeTraslado = useCallback(
    async (
      trasladoFormValues: TransferReservationBatchFormType,
      clientBusinessId: number
    ) => {
      try {
        const ViajeTraslado =
          transferReservationBatchFormBuilder.toModel(trasladoFormValues);
        await addTrasladoMutation.mutate(ViajeTraslado, clientBusinessId);
      } catch (error) {
        // El error ya es manejado por useSuperMutation y se muestra al usuario
        // Este catch evita que errores no controlados rompan la pantalla
        console.error('Error al crear viaje traslado', error);
      }
    },
    [addTrasladoMutation]
  );

  function estimatePrice(
    trasladoFormValues: TransferReservationBatchFormType,
    clientBusinessId: number
  ): Promise<TravelEstimationResult[]> {
    const estimationsPromise = trasladoFormValues.viajes.map((viaje, index) => {
      if (viaje.tipoServicio === ViajeTrasladoTipoServicio.SERVICIOS_GRUPALES) {
        return {
          isServiceGrupal: true,
          viajeIndex: index + 1,
          message: `Es Servicio Grupal*`
        };
      }
      return calculateDistanceAndCost(
        {
          arrivalDate: viaje.fechaYHoraDeLlegada,
          hasOutSite: viaje.hasOutSite,
          points: viaje.puntos.map((p) => ({
            lat: p.domicilio.latitud,
            lng: p.domicilio.longitud,
            minutesWaiting: Number(p.espera)
          })),
          serviceType: viaje.tipoServicio,
          rateName: viaje.fixedRate?.name,
          payWithCash: trasladoFormValues.pay_with_cash,
          entryChannel: trasladoFormValues.canalIngreso
        },
        clientBusinessId
      );
    });
    return Promise.all(estimationsPromise);
  }

  const getOptionsBuses = useCallback(async () => {
    const buses = await ViajeTrasladoService.getOptionsBuses();
    setOptionsBuses(buses);
  }, [ViajeTrasladoService]);

  useEffect(() => {
    getOptionsBuses();
  }, []);

  return (
    <>
      <PageHeader area="Operaciones - C.D.R." title="Crear Corporativa" />
      <PageContainer>
        <Card className="w-full" title="Crear Reserva Corporativa">
          <div className="p-8">
            <ReservaTrasladoFormWrapper<TransferReservationBatchFormType>
              onSubmit={createViajeTraslado}
              calculateDistanceAndCost={estimatePrice}
              isDraftPage={isDraftPage}
            >
              {(params) => (
                <TransferReservationBatchForm
                  isDraftPage={isDraftPage}
                  optionsBuses={optionsBuses}
                  selectedEventId={eventIdFromState}
                  selectedClientId={clientIdFromState}
                  {...params}
                />
              )}
            </ReservaTrasladoFormWrapper>
          </div>
          <CreatedReservationsModal
            open={modalOpen}
            reservationsData={createdReservationsData}
            onFinish={() => {
              setModalOpen(false);
              setTimeout(() => {
                history.push('/main/operations/coordination/pending-trips');
              }, 300);
            }}
          />
        </Card>
      </PageContainer>
    </>
  );
};
